import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Icon from '../components/Icon';
import { fetchSupportTickets, fetchSupportTicket, patchSupportTicket } from '../services/api';

const STATUS_BADGE = {
  open:        { cls: 'badge-warning', label: 'Open' },
  in_progress: { cls: 'badge-info',    label: 'In progress' },
  resolved:    { cls: 'badge-success', label: 'Resolved' },
};

const SLA_TARGETS_MS = {
  emergency: 2 * 60 * 60 * 1000,    // 2 hours
  general:   24 * 60 * 60 * 1000,   // 24 hours
};

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const fmtAbs = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const SLABadge = ({ ticket }) => {
  if (ticket.status === 'resolved') {
    return <span className="badge badge-muted">Resolved · {fmtAgo(ticket.resolved_at)}</span>;
  }
  const target = SLA_TARGETS_MS[ticket.sla_tier] || SLA_TARGETS_MS.general;
  const elapsed = Date.now() - new Date(ticket.first_received).getTime();
  const ratio = elapsed / target;
  let cls = 'badge-success';
  let txt = '';
  const remainMs = target - elapsed;
  const remainH = Math.max(0, Math.floor(remainMs / 3600000));
  const remainM = Math.max(0, Math.floor((remainMs % 3600000) / 60000));
  if (ratio >= 1) {
    cls = 'badge-danger';
    txt = `Overdue · target ${ticket.sla_tier === 'emergency' ? '2h' : '24h'}`;
  } else if (ratio >= 0.5) {
    cls = 'badge-warning';
    txt = `${remainH}h ${remainM}m left · ${ticket.sla_tier}`;
  } else {
    cls = 'badge-success';
    txt = `${remainH}h ${remainM}m left · ${ticket.sla_tier}`;
  }
  return <span className={`badge ${cls}`}>{txt}</span>;
};

const TicketRow = ({ ticket, onSelect, selected }) => {
  const sb = STATUS_BADGE[ticket.status] || STATUS_BADGE.open;
  return (
    <button
      type="button"
      onClick={() => onSelect(ticket)}
      style={{
        textAlign: 'left',
        width: '100%',
        background: selected ? 'var(--teal-faint)' : 'var(--bg-elevated)',
        border: `1px solid ${selected ? 'var(--teal)' : 'var(--bd)'}`,
        borderRadius: 'var(--r-md)',
        padding: 'var(--s3) var(--s4)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div className="row row-gap-3" style={{ justifyContent: 'space-between' }}>
        <div className="font-semibold text-md" style={{ color: 'var(--tx1)' }}>{ticket.subject || '(no subject)'}</div>
        <SLABadge ticket={ticket} />
      </div>
      <div className="row row-gap-3" style={{ justifyContent: 'space-between' }}>
        <div className="text-sm" style={{ color: 'var(--tx2)' }}>
          {ticket.from_name ? `${ticket.from_name} · ` : ''}{ticket.from_email}
        </div>
        <div className="row row-gap-2">
          <span className={`badge ${sb.cls}`}>{sb.label}</span>
          <span className="text-xs" style={{ color: 'var(--tx3)' }}>{fmtAgo(ticket.last_message_at)}</span>
        </div>
      </div>
      {ticket.last_message_preview && (
        <div className="text-sm preview-line" style={{ color: 'var(--tx3)' }}>
          {ticket.last_message_preview}
        </div>
      )}
    </button>
  );
};

const TicketDetailModal = ({ ticket, onPatch, busy, onClose, error }) => {
  if (!ticket) return null;
  const last = ticket.messages?.[ticket.messages.length - 1];
  const replyTo = ticket.from_email || last?.from_email;
  const replySubject = ticket.subject?.startsWith('Re:') ? ticket.subject : `Re: ${ticket.subject || ''}`;
  const replyBody = `\n\n— Coynitt Support\n\n----- Original -----\nFrom: ${last?.from_name || ''} <${last?.from_email || ''}>\nReceived: ${last?.received_at || ''}\n\n${(last?.body_text || '').slice(0, 800)}`;
  const mailto = `mailto:${encodeURIComponent(replyTo || '')}?subject=${encodeURIComponent(replySubject)}&body=${encodeURIComponent(replyBody)}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 'var(--s6)', overflow: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: '100%', maxWidth: 760,
          marginTop: 'var(--s4)',
          padding: 'var(--s6)',
          display: 'flex', flexDirection: 'column', gap: 'var(--s4)',
        }}
      >
        <div className="row row-gap-3" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--tx1)', marginBottom: 6, wordBreak: 'break-word' }}>
              {ticket.subject}
            </h2>
            <div className="row row-gap-2" style={{ color: 'var(--tx3)', fontSize: 13, flexWrap: 'wrap' }}>
              <span>{ticket.from_name || ticket.from_email}</span>
              <span>·</span>
              <span>{fmtAbs(ticket.first_received)}</span>
              <span>·</span>
              <SLABadge ticket={ticket} />
              <span>·</span>
              <span className={`badge ${(STATUS_BADGE[ticket.status] || STATUS_BADGE.open).cls}`}>
                {(STATUS_BADGE[ticket.status] || STATUS_BADGE.open).label}
              </span>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close ticket">
            <Icon name="x" size={18} />
          </button>
        </div>

        {error && (
          <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
          <a className="btn btn-primary" href={mailto}>
            <Icon name="send" size={14} /> Reply via email client
          </a>
          {ticket.status !== 'in_progress' && (
            <button type="button" className="btn btn-outline" disabled={busy} onClick={() => onPatch({ status: 'in_progress' })}>
              Mark in progress
            </button>
          )}
          {ticket.status !== 'resolved' && (
            <button type="button" className="btn btn-outline" disabled={busy} onClick={() => onPatch({ status: 'resolved' })}>
              Mark resolved
            </button>
          )}
          {ticket.status === 'resolved' && (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onPatch({ status: 'open' })}>
              Reopen
            </button>
          )}
          {ticket.sla_tier === 'general' ? (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onPatch({ sla_tier: 'emergency' })}>
              Flag as emergency
            </button>
          ) : (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => onPatch({ sla_tier: 'general' })}>
              Demote to general
            </button>
          )}
        </div>

        <div className="col col-gap-3" style={{ paddingTop: 'var(--s3)', borderTop: '1px solid var(--bd)' }}>
          {(ticket.messages || []).map((m) => (
            <div key={m.id} style={{ background: 'var(--bg-canvas)', border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', padding: 'var(--s3) var(--s4)' }}>
              <div className="row row-gap-3" style={{ justifyContent: 'space-between', marginBottom: 'var(--s2)' }}>
                <div className="font-semibold text-sm" style={{ color: 'var(--tx1)' }}>{m.from_name || m.from_email}</div>
                <div className="text-xs" style={{ color: 'var(--tx3)' }}>{fmtAbs(m.received_at)}</div>
              </div>
              <div className="text-sm" style={{ color: 'var(--tx2)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.body_text}</div>
            </div>
          ))}
          {(!ticket.messages || ticket.messages.length === 0) && (
            <div className="text-sm" style={{ color: 'var(--tx3)' }}>No messages on this thread yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { id: 'all',         label: 'All' },
  { id: 'open',        label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'resolved',    label: 'Resolved' },
];

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('open');
  const [slaFilter, setSlaFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSupportTickets();
      setTickets(data.tickets || []);
      setMeta(data.meta || {});
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); setDetailError(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    fetchSupportTicket(selectedId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setDetailError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const filtered = useMemo(() => tickets.filter((t) => {
    if (activeTab !== 'all' && t.status !== activeTab) return false;
    if (slaFilter !== 'all' && t.sla_tier !== slaFilter) return false;
    return true;
  }), [tickets, activeTab, slaFilter]);

  const counts = useMemo(() => ({
    all: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    emergency: tickets.filter((t) => t.sla_tier === 'emergency' && t.status !== 'resolved').length,
  }), [tickets]);

  const onPatch = async (patch) => {
    if (!detail?.id) return;
    setBusy(true);
    setDetailError(null);
    try {
      const updated = await patchSupportTicket(detail.id, patch);
      setDetail((d) => ({ ...d, ...updated }));
      setTickets((arr) => arr.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    } catch (e) {
      setDetailError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const closeDetail = () => { setSelectedId(null); setDetail(null); setDetailError(null); };

  const imapNotConfigured = meta?.imap_configured === false && meta?.source === 'supabase';
  const inMockMode = meta?.source === 'mock';

  return (
    <>
      <div className="page-head">
        <h1>Support</h1>
        <p>support@coynitt.ca · IMAP-polled inbox · SLA tracking</p>
      </div>

      {(imapNotConfigured || inMockMode) && (
        <div className="card" style={{ marginBottom: 'var(--s5)', borderColor: 'var(--warn)', background: 'var(--warn-bg)' }}>
          <div className="row row-gap-3" style={{ alignItems: 'flex-start' }}>
            <Icon name="bell" size={20} />
            <div>
              <div className="font-semibold text-sm" style={{ color: 'var(--tx1)' }}>
                {inMockMode ? 'Mock data (Supabase not configured)' : 'IMAP not configured'}
              </div>
              <div className="text-sm" style={{ color: 'var(--tx2)', marginTop: 4 }}>
                {inMockMode
                  ? 'Running against deterministic mock tickets for local dev.'
                  : 'Set SUPPORT_IMAP_HOST / PORT / USER / PASS in Vercel env to start polling support@coynitt.ca every 5 minutes.'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-grid mb-4">
        <div className="card"><div className="card-title">Open</div><div className="stat-value">{counts.open}</div></div>
        <div className="card"><div className="card-title">In progress</div><div className="stat-value" style={{ color: 'var(--info)' }}>{counts.in_progress}</div></div>
        <div className="card"><div className="card-title">Resolved</div><div className="stat-value" style={{ color: 'var(--ok)' }}>{counts.resolved}</div></div>
        <div className="card"><div className="card-title">Emergency open</div><div className="stat-value" style={{ color: counts.emergency > 0 ? 'var(--err)' : 'var(--ok)' }}>{counts.emergency}</div></div>
      </div>

      <div className="card">
        <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16 }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px', background: 'transparent',
                color: activeTab === tab.id ? 'var(--teal)' : 'var(--tx2)',
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: 13,
                borderBottom: activeTab === tab.id ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1, cursor: 'pointer',
              }}
            >
              {tab.label}
              <span className="badge badge-muted" style={{ marginLeft: 8 }}>{counts[tab.id] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="row row-gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
          <select value={slaFilter} onChange={(e) => setSlaFilter(e.target.value)} aria-label="Filter by SLA tier" style={selectStyle}>
            <option value="all">All SLA tiers</option>
            <option value="emergency">Emergency</option>
            <option value="general">General</option>
          </select>
        </div>

        {error && (
          <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>{error}</div>
        )}

        {loading && tickets.length === 0 ? (
          <div className="col col-gap-2">
            {[0, 1, 2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 64 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-icon"><Icon name="inbox" size={26} /></div>
            <h3>No tickets in this view</h3>
            <p>{tickets.length === 0 ? 'No tickets have arrived yet.' : "You're all caught up."}</p>
          </div>
        ) : (
          <div className="stagger" style={{ display: 'grid', gap: 8 }}>
            {filtered.map((t) => (
              <TicketRow key={t.id} ticket={t} onSelect={(x) => setSelectedId(x.id)} selected={selectedId === t.id} />
            ))}
          </div>
        )}
      </div>

      {selectedId && (detail || detailLoading || detailError) && (
        detail ? (
          <TicketDetailModal
            ticket={detail}
            onPatch={onPatch}
            busy={busy}
            onClose={closeDetail}
            error={detailError}
          />
        ) : (
          <div role="dialog" aria-modal="true" onClick={closeDetail} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--s6)' }}>
            <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 480, padding: 'var(--s6)', textAlign: 'center' }}>
              {detailError ? (
                <>
                  <div className="empty-icon" style={{ margin: '0 auto var(--s3)', background: 'var(--err-bg)', color: 'var(--err)' }}>
                    <Icon name="x" size={26} />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>Couldn't load ticket</h3>
                  <p className="text-sm" style={{ color: 'var(--tx2)', marginBottom: 'var(--s4)' }}>{detailError}</p>
                  <button type="button" className="btn btn-outline" onClick={closeDetail}>Close</button>
                </>
              ) : (
                <div className="text-sm" style={{ color: 'var(--tx3)' }}>Loading ticket…</div>
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};

const selectStyle = {
  padding: '10px 14px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  color: 'var(--tx1)',
  fontSize: 13,
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  outline: 'none',
};

export default Support;
